import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, Dialog, IconButton } from "@mui/material";

import {
    IconBuildingBank,
    IconKey,
    IconMail,
    IconPhone,
    IconId,
    IconX,
    IconMoneybagMove,
} from "@tabler/icons-react";

import api from "@helpers/api.js";
import Parser from "@helpers/parser.js";
import banks from "@helpers/banks.js";
import { getIntlLocale } from "@i18n";

import { Table, Text, StatusChip, Button } from "@fragments";

import "./styles.scss";

export default function WithdrawalsTable({ provider = 'asaas', currency = 'BRL' }) {
    const { t } = useTranslation("banking");

    // Estados de dados e loading centralizados
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal de motivo da falha
    const [failReasonModal, setFailReasonModal] = useState({ open: false, reason: '' });

    // Estados de paginação
    const [pagination, setPagination] = useState({
        pages: 0,
        records: 0,
        page: 1
    });

    const rowsSize = 15;

    const fetchData = useCallback(async (pageNumber = pagination.page) => {
        try {
            setLoading(true);

            const params = new URLSearchParams();

            // Adicionar parâmetros de paginação
            params.append('page', pageNumber.toString());
            params.append('per_page', rowsSize);
            params.append('provider', provider);

            const { data } = await api.get(`wallet/withdrawal/list?${params.toString()}`);

            setWithdrawals(data.data || []);

            // Atualizar dados de paginação
            setPagination({
                pages: data.last_page || 0,
                records: data.total || 0,
                page: data.current_page || 1
            });

        } catch (error) {
            console.error('Error fetching withdrawals:', error);
            setWithdrawals([]);
            setPagination({
                pages: 0,
                records: 0,
                page: 1
            });
        } finally {
            setLoading(false);
        }
    }, [pagination.page, provider]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchData(1);
    }, [provider]);

    const handlePageChange = useCallback((newPage) => {
        fetchData(newPage);
    }, [fetchData]);

    const getStatusComponent = (status) => {
        const StatusComponent = {
            'success': StatusChip.Success,
            'failed': StatusChip.Error,
            'default': StatusChip.Default,
        }[status || 'default'];

        const statusLabels = {
            'success': t('common:status.paid'),
            'failed': t('withdrawals.table.statusFailed'),
        };

        return <StatusComponent text={statusLabels[status] || t('common:status.unknown')} />;
    };

    const columns = [
        {
            id: 'value',
            title: t('withdrawals.table.amount'),
            grow: 1,
            customRender: (data) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text.h5 contrast={7} weight={500} style={{ width: '100px' }}>
                        {Parser.price.format(data.value?.amount, data.value?.currency || currency)}
                    </Text.h5>
                    <Text.span contrast={5} weight={400}>{data.value?.currency || currency}</Text.span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Tooltip title={data.status === 'failed' ? (data.result?.fail_reason || t('withdrawals.table.unknownFailure')) : ''} arrow>
                            <div>
                                {getStatusComponent(data.status)}
                            </div>
                        </Tooltip>
                        {data.status === 'failed' && (
                            <Text.span 
                                contrast={4} 
                                weight={500} 
                                className="on-hover-underline"
                                style={{ 
                                    cursor: 'pointer', 
                                    fontSize: '12px',
                                    whiteSpace: 'nowrap',
                                    textDecoration: 'underline'
                                }} 
                                onClick={() => setFailReasonModal({ open: true, reason: data.result?.fail_reason || t('withdrawals.table.unknownFailure') })}
                            >
                                {t('withdrawals.table.viewReason')}
                            </Text.span>
                        )}
                    </div>
                </div>
            )
        },
        {
            id: 'destination',
            title: t('withdrawals.table.destination'),
            grow: 2,
            customRender: (data) => {
                const rowProvider = data.provider || provider;
                if (rowProvider === 'stripe') {
                    return (
                        <div className="condensed-banking-info" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', width: '100%', overflow: 'hidden' }}>
                            <div className="bank-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                <IconBuildingBank size={20} style={{ color: 'var(--text-contrast-5)' }} />
                                <Text.span contrast={7} weight={500}>Stripe Express</Text.span>
                            </div>
                            <Text.span contrast={2} style={{ flexShrink: 0 }}>•</Text.span>
                            <Text.span contrast={5} weight={400}>
                                {t('withdrawals.table.stripePayout')}
                            </Text.span>
                        </div>
                    );
                }

                const withdrawal = data.wallet?.withdrawal || {};
                const receiver = withdrawal.receiver || {};
                const bankCode = receiver.bank?.code;
                const bank = banks.find(b => parseInt(b.code) === parseInt(bankCode));
                const owner = receiver.owner || {};

                const keyType = withdrawal.keyType;
                const keyValue = withdrawal.key;
                let keyFormatted = keyValue;
                let KeyIcon = IconKey;

                if (keyType === 'email') {
                    keyFormatted = Parser.email(keyValue);
                    KeyIcon = IconMail;
                } else if (keyType === 'phone') {
                    keyFormatted = Parser.phone('+55' + keyValue);
                    KeyIcon = IconPhone;
                } else if (keyType === 'cpf') {
                    keyFormatted = Parser.cpf(keyValue);
                    KeyIcon = IconId;
                } else if (keyType === 'cnpj') {
                    keyFormatted = Parser.cnpj(keyValue);
                    KeyIcon = IconId;
                } else if (keyType === 'evp') {
                    keyFormatted = keyValue;
                    KeyIcon = IconKey;
                }

                return (
                    <div className="condensed-banking-info" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', width: '100%', overflow: 'hidden' }}>
                        <div className="bank-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            {bank?.logo ? (
                                <img src={bank.logo} alt={bank.name} style={{ width: '20px', height: '20px', objectFit: 'contain', borderRadius: '50%' }} />
                            ) : (
                                <IconBuildingBank size={20} style={{ color: 'var(--text-contrast-5)' }} />
                            )}
                            <Text.span contrast={7} weight={500} className="text-uppercase">{bank?.nick || bank?.name || t('withdrawals.table.unknownBank')}</Text.span>
                        </div>

                        <Text.span contrast={2} style={{ flexShrink: 0 }}>•</Text.span>

                        <div className="key-row" style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                            <KeyIcon size={14} style={{ opacity: 0.6 }} />
                            <Text.span contrast={5} weight={400}>
                                {keyFormatted || '-'}
                            </Text.span>
                        </div>

                        <Text.span contrast={2} style={{ flexShrink: 0 }}>•</Text.span>

                        <div className="owner-row" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                            <Text.span contrast={5}>{owner.name || '-'}</Text.span>
                        </div>
                    </div>
                );
            }
        },
        {
            id: 'date',
            title: t('withdrawals.table.date'),
            render: (data) => {
                if (!data.timestamps?.created) return '-';
                const date = new Date(data.timestamps.created);
                return date.toLocaleDateString(getIntlLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
            }
        }
    ];

    return (
        <div className="withdrawals-table mt-3 mb-6">
            <Text.h3 weight={500} left={<IconMoneybagMove />}>
                {t('withdrawals.table.title')}
            </Text.h3>
            <Table
                id="banking-withdrawals-history-table"
                columns={columns}
                data={withdrawals}
                loading={loading}
                loadingRows={4}
                contained={true}
                className="mt-2"
                emptyMessage={t('withdrawals.table.empty')}
                enablePagination={true}
                pages={pagination.pages}
                records={pagination.records}
                page={pagination.page}
                onSelectPage={handlePageChange}
            />
            <Dialog 
                open={failReasonModal.open} 
                onClose={() => setFailReasonModal({ open: false, reason: '' })}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: '12px',
                        padding: '8px'
                    }
                }}
            >
                <div style={{ padding: '16px 16px 8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text.h4 weight={500}>{t('withdrawals.table.failReasonTitle')}</Text.h4>
                    <IconButton size="small" onClick={() => setFailReasonModal({ open: false, reason: '' })}>
                        <IconX style={{ color: 'var(--text-contrast-7)' }} />
                    </IconButton>
                </div>
                <div style={{ padding: '8px 16px 24px 16px' }}>
                    <Text.span contrast={5}>
                        {failReasonModal.reason}
                    </Text.span>
                </div>
                <div style={{ padding: '0 16px 16px 16px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" color="primary" onClick={() => setFailReasonModal({ open: false, reason: '' })}>
                        {t('common:actions.close')}
                    </Button>
                </div>
            </Dialog>
        </div>
    )
}
